import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { authenticate, matchesSignupAccount, rememberSignup, signupDestination } from '../utils/signupFlow';
import SignUpLink from '../components/auth/SignUpLink';
import '../styles/pages/LoginPage.css';

const pool = new CognitoUserPool({ UserPoolId: 'eu-west-2_nyRkWiHVC', ClientId: 'fsp09nn4h6a084bod215if65q' });

export default function LoginPage() {
  const { isAuthenticated, authInitialized, accountReady, userAttributes, setIsAuthenticated, fetchUserAttributes } = useAuth();
  const { connectWebSocket } = useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const signup = location.state?.signup;
  const [email, setEmail] = useState(signup?.email || location.state?.email || '');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [accountError, setAccountError] = useState('');
  const [loading, setLoading] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(false);
  const operation = useRef({ id: 0 });
  const actions = useRef();
  useEffect(() => {
    mounted.current = true;
    const request = operation.current;
    return () => { mounted.current = false; request.id++; };
  }, []);

  const next = new URLSearchParams(location.search).get('next');
  const nextPath = next?.split(/[?#]/)[0];
  const destination = next?.startsWith('/') && !next.startsWith('//') &&
    !next.includes('\\') && !['/', '/login'].includes(nextPath) ? next : '/home';
  const current = id => mounted.current && id === operation.current.id;
  const begin = () => {
    if (pending.current) return null;
    pending.current = true;
    setLoading(true); setErr(''); setAccountError('');
    return ++operation.current.id;
  };
  const finish = id => {
    if (!current(id)) return;
    pending.current = false; setLoading(false);
  };
  const loadAccount = async id => {
    try {
      const attrs = await fetchUserAttributes();
      if (!current(id)) return;
      if (!attrs?.sub) throw new Error('Your account details are not available yet.');
      if (signup && !matchesSignupAccount(attrs, signup)) {
        throw new Error('That session does not match your signup account. Log in with your signup email to continue.');
      }
      connectWebSocket(attrs.sub);
      navigate(signup ? signupDestination(attrs) : destination, { replace: true });
    } catch (error) {
      if (!current(id)) return;
      if (error.code === 'email_verification_required') {
        navigate('/verify-email', { replace: true, state: {
          email: error.email || email, signupSub: error.sub || signup?.signupSub,
          attributeVerification: true, returnTo: signup ? undefined : destination,
        } });
      } else setAccountError(error.message || 'We could not load your account. Please try again.');
    }
  };
  const retryAccount = async () => {
    const id = begin();
    if (id === null) return;
    await loadAccount(id);
    finish(id);
  };
  actions.current = { retryAccount };
  useEffect(() => {
    if (authInitialized && isAuthenticated && (!userAttributes?.sub || accountReady === false) && !loading && !accountError) {
      actions.current.retryAccount();
    }
  }, [authInitialized, isAuthenticated, accountReady, userAttributes?.sub, loading, accountError]);

  const onSubmit = async event => {
    event.preventDefault();
    const id = begin();
    if (id === null) return;
    const loginEmail = email.trim();
    try {
      const user = new CognitoUser({ Username: loginEmail, Pool: pool });
      const result = await authenticate(user, loginEmail, password);
      if (!current(id)) return;
      localStorage.setItem('authToken', result.getAccessToken().getJwtToken());
      setPassword('');
      setIsAuthenticated(true);
      await loadAccount(id);
    } catch (error) {
      if (!current(id)) return;
      if (error.code === 'UserNotConfirmedException') {
        const signupId = rememberSignup(loginEmail, password);
        navigate('/verify-email', { replace: true, state: { email: loginEmail, signupId, signupSub: signup?.signupSub } });
      } else {
        setErr(error.code === 'NotAuthorizedException' || error.code === 'UserNotFoundException'
          ? 'Invalid email or password' : 'Sign-in did not finish. Please try again, or contact ViewFinders if you need help.');
      }
    } finally { finish(id); }
  };

  if (!authInitialized) return <p role="status">Loading your account…</p>;
  if (isAuthenticated && accountReady !== false && !loading && !accountError && userAttributes?.sub && (!signup || matchesSignupAccount(userAttributes, signup))) {
    return <Navigate to={signup ? signupDestination(userAttributes) : destination} replace />;
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>Log in</h1>
        {signup && <p>Log in as <strong>{signup.email}</strong> to continue your signup.</p>}
        {signup && isAuthenticated && userAttributes?.sub && !matchesSignupAccount(userAttributes, signup) &&
          <p role="status">A different account is signed in. Enter the password for your signup account to continue.</p>}
        {accountError && <div role="alert">
          <p>{accountError}</p>
          <button type="button" className="btn btn--secondary btn--md" onClick={retryAccount} disabled={loading}>Retry loading account</button>
        </div>}
        <form onSubmit={onSubmit} className="auth-form" aria-busy={loading}>
          <label htmlFor="login-email">Email</label>
          <input id="login-email" type="email" value={email} onChange={event => { setEmail(event.target.value); setErr(''); }}
            autoComplete="username" required readOnly={Boolean(signup)} disabled={loading} />
          <label htmlFor="login-password">Password</label>
          <input id="login-password" type="password" value={password} onChange={event => { setPassword(event.target.value); setErr(''); }}
            autoComplete="current-password" required disabled={loading} />
          {err && <p className="auth-error" role="alert">{err}</p>}
          {loading && <p role="status">Signing in and loading your account…</p>}
          <button type="submit" className="btn btn--primary btn--md" disabled={loading}>{loading ? 'Signing in…' : 'Log in'}</button>
        </form>
        <div className="auth-footer"><span>No account?</span><SignUpLink variant="secondary" size="sm">Sign up</SignUpLink></div>
      </div>
    </main>
  );
}
